import { create } from 'zustand';
import type { PKMObject, SyncStatus } from '../models/types';

// Open mode: modal (overlay) or page (full navigation)
export type ObjectOpenMode = 'modal' | 'page';

interface AppState {
  // Current object being viewed/edited
  currentObject: PKMObject | null;
  setCurrentObject: (object: PKMObject | null) => void;

  // Top-level objects (for sidebar - not contained by anything)
  topLevelObjects: PKMObject[];
  setTopLevelObjects: (objects: PKMObject[]) => void;
  addTopLevelObject: (object: PKMObject) => void;

  // Objects contained in the current object
  containedObjects: PKMObject[];
  setContainedObjects: (objects: PKMObject[]) => void;
  addContainedObject: (object: PKMObject) => void;

  // Update/remove operations work on all objects
  updateObjectInStore: (id: string, updates: Partial<PKMObject>) => void;
  removeObject: (id: string) => void;

  // UI State
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  // Object open mode preference (modal vs full page)
  objectOpenMode: ObjectOpenMode;
  setObjectOpenMode: (mode: ObjectOpenMode) => void;
  toggleObjectOpenMode: () => void;

  // Whether the task properties panel is expanded by default on the task page
  taskPropertiesExpanded: boolean;
  setTaskPropertiesExpanded: (expanded: boolean) => void;
  toggleTaskPropertiesExpanded: () => void;

  // Search
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  // Sync status
  syncStatus: SyncStatus;
  setSyncStatus: (status: SyncStatus) => void;

  // Container tracking for context-aware task creation
  lastActiveContainerId: string | null;
  setLastActiveContainerId: (id: string | null) => void;

  // Helper to get current container ID from various sources
  getCurrentContainerId: () => string | null;
}

export const useAppStore = create<AppState>((set) => ({
  // Current object
  currentObject: null,
  setCurrentObject: (object) => set({ currentObject: object }),

  // Top-level objects
  topLevelObjects: [],
  setTopLevelObjects: (objects) => set({ topLevelObjects: objects }),
  addTopLevelObject: (object) =>
    set((state) => ({ topLevelObjects: [object, ...state.topLevelObjects] })),

  // Contained objects
  containedObjects: [],
  setContainedObjects: (objects) => set({ containedObjects: objects }),
  addContainedObject: (object) =>
    set((state) => ({ containedObjects: [...state.containedObjects, object] })),

  // Update/remove
  updateObjectInStore: (id, updates) =>
    set((state) => ({
      topLevelObjects: state.topLevelObjects.map((obj) =>
        obj.id === id ? { ...obj, ...updates } : obj
      ),
      containedObjects: state.containedObjects.map((obj) =>
        obj.id === id ? { ...obj, ...updates } : obj
      ),
      currentObject:
        state.currentObject?.id === id
          ? { ...state.currentObject, ...updates }
          : state.currentObject,
    })),
  removeObject: (id) =>
    set((state) => ({
      topLevelObjects: state.topLevelObjects.filter((obj) => obj.id !== id),
      containedObjects: state.containedObjects.filter((obj) => obj.id !== id),
      currentObject:
        state.currentObject?.id === id ? null : state.currentObject,
    })),

  // UI State
  sidebarOpen: true,
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  // Object open mode - load from localStorage or default to 'modal'
  objectOpenMode: (localStorage.getItem('objectOpenMode') as ObjectOpenMode) || 'modal',
  setObjectOpenMode: (mode) => {
    localStorage.setItem('objectOpenMode', mode);
    set({ objectOpenMode: mode });
  },
  toggleObjectOpenMode: () => set((state) => {
    const newMode = state.objectOpenMode === 'modal' ? 'page' : 'modal';
    localStorage.setItem('objectOpenMode', newMode);
    return { objectOpenMode: newMode };
  }),

  // Task properties expanded preference - load from localStorage or default to false
  taskPropertiesExpanded: localStorage.getItem('taskPropertiesExpanded') === 'true',
  setTaskPropertiesExpanded: (expanded) => {
    localStorage.setItem('taskPropertiesExpanded', String(expanded));
    set({ taskPropertiesExpanded: expanded });
  },
  toggleTaskPropertiesExpanded: () => set((state) => {
    const next = !state.taskPropertiesExpanded;
    localStorage.setItem('taskPropertiesExpanded', String(next));
    return { taskPropertiesExpanded: next };
  }),

  // Search
  searchQuery: '',
  setSearchQuery: (query) => set({ searchQuery: query }),

  // Sync status
  syncStatus: {
    pendingChanges: 0,
    isSyncing: false,
  },
  setSyncStatus: (status) => set({ syncStatus: status }),

  // Container tracking - load from localStorage on init
  lastActiveContainerId: localStorage.getItem('lastActiveContainer'),
  setLastActiveContainerId: (id) => {
    if (id) {
      localStorage.setItem('lastActiveContainer', id);
    } else {
      localStorage.removeItem('lastActiveContainer');
    }
    set({ lastActiveContainerId: id });
  },

  // Get current container ID from currentObject or last active
  getCurrentContainerId: () => {
    const state = useAppStore.getState();

    // Priority 1: Current object if it's a project or area
    if (state.currentObject) {
      const { type, id } = state.currentObject;
      if (type === 'project' || type === 'area') {
        return id;
      }
    }

    // Priority 2: Last active container
    return state.lastActiveContainerId;
  },
}));
